import React, { useCallback, useEffect, useRef } from 'react';
import { throttle } from 'lodash';
import { AudioPlayer } from '@atlassian/learning-components';
import { useLoaderData } from 'react-router-dom';

import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { handlePlyrError } from 'src/pages/LessonPage/utils/plyrErrorHandlingUtils';
import { sendTrackEvent, sendUIEvent } from 'src/utils/analytics';
import { isStandalone } from 'src/pages/LessonPage/utils/isStandalone';
import { getSlug } from 'src/utils/slug';
import { LessonLoaderProps } from 'src/pages/LessonPage/loader';

import { sanitizeAudioUrl } from './utils/linkSanitizer';

export interface AudioProps {
  isOpened: boolean;
  onClose: () => void;
}

export const Audio: React.FC<AudioProps> = ({ isOpened, onClose }) => {
  const { user } = useAtlassianUser();

  const { course, lesson } = useLoaderData() as LessonLoaderProps;

  const lastActionRef = useRef<string | undefined>(undefined);
  const audioProgressRef = useRef<number>(NaN);

  const courseSlug = getSlug(course);
  const lessonSlug = getSlug(lesson);
  const audioUrl = (lesson?.audioPlayer?.orangeLogicAsset?.imageUrl ?? lesson?.audioPlayer?.cdnUrl) || '';
  const sanitizedAudioUrl = sanitizeAudioUrl(audioUrl);
  const handleControlClick = useCallback(
    (type: string) => {
      lastActionRef.current = type;

      sendUIEvent(
        'lessonScreen',
        'button',
        'clicked',
        'audioPlayButton',
        {
          audioId: lesson?.audioPlayer?.sys.id,
          courseSlug,
          lessonSlug,
          type,
          isStandaloneLesson: isStandalone(),
        },
        user?.account_id,
      );
    },
    [lesson, user, courseSlug, lessonSlug],
  );

  // throttle, because plyr sometimes fires it multiple times
  const handleAudioProgressUpdate = throttle((audioProgress: number) => {
    audioProgressRef.current = audioProgress;

    if (audioProgress === 100) {
      sendTrackEvent(
        'lessonScreen',
        'audio',
        'completed',
        {
          courseSlug,
          lessonSlug,
          audioProgress: audioProgressRef.current,
        },
        user?.account_id,
      );
    }
  }, 500);

  useEffect(() => {
    const handleBeforeUnload = (): void => {
      const isMediaStartedButNotEnded = audioProgressRef.current !== 100 && audioProgressRef.current > 1;

      if (isMediaStartedButNotEnded) {
        sendTrackEvent(
          'lessonScreen',
          'audio',
          'abandoned',
          {
            courseSlug,
            lessonSlug,
            audioProgress: audioProgressRef.current,
          },
          user?.account_id,
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return (): void => {
      handleBeforeUnload();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [courseSlug, lessonSlug, user]);

  const handleError = useCallback((plyrError: Plyr.PlyrEvent) => {
    handlePlyrError('audio', plyrError, { lastAction: lastActionRef.current });
  }, []);

  useEffect(() => {
    lastActionRef.current = undefined;
    audioProgressRef.current = NaN;
  }, [lesson, course]);

  return (
    <AudioPlayer
      source={sanitizedAudioUrl}
      onControlClick={handleControlClick}
      onProgressUpdate={handleAudioProgressUpdate}
      onError={handleError}
      key={lesson?.sys.id}
      onClose={onClose}
      isOpened={isOpened}
    />
  );
};
